import type {
    AnalyticsSeedInterface,
    ProjectAnalyticsInterface,
    SprintAnalyticsInterface,
    TeamAnalyticsInterface,
} from "@/interfaces/analytics";

const C = {
    primary: "var(--color-primary-medium)",
    success: "var(--color-success)",
    warning: "var(--color-warning)",
    error: "var(--color-error)",
    info: "var(--color-info)",
    muted: "var(--color-muted-foreground)",
};

const fallbackProject: ProjectAnalyticsInterface = {
    completion_rate: 58,
    sprints_total: 4,
    sprints_active: 1,
    tasks_done: 42,
    tasks_total: 72,
    open_blockers: 3,
    on_time_rate: 81,
    velocity_trend: [
        { label: "S-1", value: 22 },
        { label: "S-2", value: 28 },
        { label: "S-3", value: 26 },
        { label: "S-4", value: 31 },
    ],
    burn_chart: [
        { label: "D1", planned: 100, actual: 98 },
        { label: "D3", planned: 84, actual: 90 },
        { label: "D5", planned: 68, actual: 76 },
        { label: "D7", planned: 52, actual: 58 },
        { label: "D9", planned: 36, actual: 40 },
        { label: "D11", planned: 20, actual: 24 },
        { label: "D14", planned: 0, actual: 8 },
    ],
    status_breakdown: [
        { name: "Done", value: 42, color: C.success },
        { name: "In Progress", value: 14, color: C.primary },
        { name: "Review", value: 8, color: C.info },
        { name: "Blocked", value: 3, color: C.error },
        { name: "Backlog", value: 5, color: C.muted },
    ],
    blocker_breakdown: [
        { name: "Requirements", value: 4, color: "var(--color-friction-requirements)" },
        { name: "Dependencies", value: 3, color: "var(--color-friction-dependencies)" },
        { name: "Process", value: 2, color: "var(--color-friction-process)" },
        { name: "Team", value: 1, color: "var(--color-friction-team)" },
    ],
    contributors: [
        { name: "Layla Hassan", initials: "LH", tasks_done: 12, hours: 38 },
        { name: "Omar Farouk", initials: "OF", tasks_done: 10, hours: 32 },
        { name: "Sara Mostafa", initials: "SM", tasks_done: 8, hours: 26 },
        { name: "Youssef Adel", initials: "YA", tasks_done: 6, hours: 22 },
    ],
};

const fallbackSprint: SprintAnalyticsInterface = {
    completion_rate: 64,
    tasks_done: 16,
    tasks_total: 25,
    story_points_done: 38,
    story_points_total: 60,
    days_left: 4,
    open_blockers: 2,
    on_time_rate: 78,
    burn_chart: [
        { label: "D1", planned: 60, actual: 58 },
        { label: "D3", planned: 50, actual: 54 },
        { label: "D5", planned: 40, actual: 45 },
        { label: "D7", planned: 30, actual: 32 },
        { label: "D9", planned: 20, actual: 22 },
        { label: "D11", planned: 10, actual: 14 },
        { label: "D14", planned: 0, actual: 8 },
    ],
    daily_throughput: [
        { label: "Mon", value: 2 },
        { label: "Tue", value: 4 },
        { label: "Wed", value: 3 },
        { label: "Thu", value: 5 },
        { label: "Fri", value: 2 },
    ],
    status_breakdown: [
        { name: "Done", value: 16, color: C.success },
        { name: "In Progress", value: 5, color: C.primary },
        { name: "Review", value: 2, color: C.info },
        { name: "Blocked", value: 2, color: C.error },
    ],
    contributors: [
        { name: "Layla Hassan", initials: "LH", tasks_done: 6, hours: 18 },
        { name: "Omar Farouk", initials: "OF", tasks_done: 5, hours: 14 },
        { name: "Sara Mostafa", initials: "SM", tasks_done: 3, hours: 9 },
        { name: "Youssef Adel", initials: "YA", tasks_done: 2, hours: 6 },
    ],
};

const fallbackTeam: TeamAnalyticsInterface = {
    members_active: 6,
    projects_active: 2,
    sprints_active: 3,
    tasks_done: 112,
    tasks_total: 160,
    on_time_rate: 83,
    velocity_trend: [
        { label: "W-1", value: 18 },
        { label: "W-2", value: 22 },
        { label: "W-3", value: 26 },
        { label: "W-4", value: 24 },
        { label: "W-5", value: 30 },
        { label: "W-6", value: 28 },
    ],
    workload: [
        { name: "Layla Hassan", initials: "LH", tasks_done: 18, hours: 62 },
        { name: "Omar Farouk", initials: "OF", tasks_done: 15, hours: 50 },
        { name: "Sara Mostafa", initials: "SM", tasks_done: 12, hours: 42 },
        { name: "Youssef Adel", initials: "YA", tasks_done: 10, hours: 38 },
        { name: "Nour Hany", initials: "NH", tasks_done: 8, hours: 28 },
        { name: "Karim Zaki", initials: "KZ", tasks_done: 6, hours: 22 },
    ],
    focus_breakdown: [
        { name: "Feature", value: 48, color: C.primary },
        { name: "Bug", value: 18, color: C.error },
        { name: "Chore", value: 14, color: C.muted },
        { name: "Spike", value: 8, color: C.info },
    ],
};

export const analyticsSeed: AnalyticsSeedInterface = {
    projects: {
        "pr-01": {
            ...fallbackProject,
            completion_rate: 71,
            on_time_rate: 88,
            tasks_done: 58,
            tasks_total: 82,
            sprints_total: 6,
            sprints_active: 1,
            open_blockers: 2,
            velocity_trend: [
                { label: "S-1", value: 22 },
                { label: "S-2", value: 28 },
                { label: "S-3", value: 30 },
                { label: "S-4", value: 34 },
                { label: "S-5", value: 32 },
                { label: "S-6", value: 36 },
            ],
        },
        "pr-02": {
            ...fallbackProject,
            completion_rate: 65,
            on_time_rate: 74,
            tasks_done: 36,
            tasks_total: 56,
            sprints_total: 4,
            sprints_active: 1,
            open_blockers: 4,
        },
        "pr-03": {
            ...fallbackProject,
            completion_rate: 42,
            on_time_rate: 65,
            tasks_done: 18,
            tasks_total: 44,
            sprints_total: 3,
            sprints_active: 1,
            open_blockers: 5,
        },
    },
    sprints: {
        "sp-01": {
            ...fallbackSprint,
            completion_rate: 88,
            tasks_done: 22,
            tasks_total: 25,
            on_time_rate: 92,
            open_blockers: 0,
            days_left: 0,
        },
        "sp-02": {
            ...fallbackSprint,
            completion_rate: 52,
            tasks_done: 12,
            tasks_total: 23,
            on_time_rate: 70,
            open_blockers: 3,
            days_left: 6,
        },
        "sp-03": {
            ...fallbackSprint,
            completion_rate: 30,
            tasks_done: 6,
            tasks_total: 20,
            on_time_rate: 55,
            open_blockers: 4,
            days_left: 10,
        },
    },
    teams: {
        "t-01": {
            ...fallbackTeam,
            members_active: 8,
            projects_active: 3,
            sprints_active: 4,
            tasks_done: 182,
            tasks_total: 240,
            on_time_rate: 86,
        },
        "t-02": {
            ...fallbackTeam,
            members_active: 5,
            projects_active: 2,
            sprints_active: 2,
            tasks_done: 74,
            tasks_total: 110,
            on_time_rate: 78,
        },
    },
    fallback: {
        project: fallbackProject,
        sprint: fallbackSprint,
        team: fallbackTeam,
    },
};
